import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconCalendarEvent,
  IconCheck,
  IconClock,
  IconFileText,
  IconMapPin,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import { fetchManagerOptions } from '../../../entities/user/model/api';
import { pb } from '../../../shared/api/pocketbase';
import { DateInput } from '../../../shared/ui/DateInput';
import { FormField } from '../../../shared/ui/FormField';
import { Select, SelectOption } from '../../../shared/ui/Select';
import { TextArea } from '../../../shared/ui/Textarea';
import { TimePicker } from '../../../shared/ui/TimePicker';
import { PinMoscowForm, MoscowPrefill } from '../../pin-moscow-form';

const REGIONS = ['Москва', 'Санкт-Петербург', 'Уфа', 'Чита'];

export interface QuickPreset {
  key: string;
  label: string;
  name: string;
  owner: 'self' | 'admin';
  region: boolean;
  date: 'pick' | 'today-allday';
  regions?: string[];           // свои варианты региона (иначе REGIONS)
  noAllDay?: boolean;           // скрыть галочку «весь день»
  arrivalForRegion?: string;    // регион, для которого нужна дата приезда
}

export const QUICK_PRESETS: QuickPreset[] = [
  { key: 'callback', label: 'Перезвон', name: 'Перезвон', owner: 'self', region: false, date: 'pick', noAllDay: true },
];

const ARRAY = [
  {value: 'Морская пехота (до 60)', label: 'Морская пехота (до 60)'},
  {value: 'Охрана стратегических объектов', label: 'Охрана стратегических объектов'},
  {value: 'Снайперы (до 60)', label: 'Снайперы (до 60)'},
  {value: 'Танковый батальон (до 60)', label: ' Танковый батальон (до 60)'},
  {value: 'Артиллерийский дивизион (до 60)', label: 'Артиллерийский дивизион (до 60)'},
  {value: 'Реактивный артиллерийский дивизион (до 60)', label: 'Реактивный артиллерийский дивизион (до 60)'},
  {value: 'Зенитный дивизион (до 60)', label: 'Зенитный дивизион (до 60)'},
  {value: 'Разведывательная рота (до 60)', label: 'Разведывательная рота (до 60)'},
  {value: 'Рота БПЛА', label: 'Рота БПЛА'},
  {value: 'Инженерно-саперная рота (до 60)', label: 'Инженерно-саперная рота (до 60)'},
  {value: 'Рота радиоэлектронной борьбы (до 60)', label: 'Рота радиоэлектронной борьбы (до 60)'},
  {value: 'Радиолокационный комплекс (до 60)', label: 'Радиолокационный комплекс (до 60)'},
  {value: 'Водителей категории C, E, D (до 60)', label: 'Водителей категории C, E, D (до 60)'},
  {value: 'Мед. батальон', label: 'Мед. батальон'},
  {value: 'Военная полиция', label: 'Военная полиция'},
  {value: 'Водитель-сварщик в ремроту', label: 'Водитель-сварщик в ремроту'}
]

interface QuickForm {
  name: string;
  region: string;
  ownerId: string;
  ownerName: string;
  planDate: string;
  arrival: string;
}

interface Props {
  preset: QuickPreset | null;
  isSaving: boolean;
  prefill?: MoscowPrefill;
  onClose: () => void;
  onCreate: (data: { name: string; ownerId: string; planDate: string }) => Promise<void>;
  // Создать несколько задач разом (анкета Москвы). Возвращает после закрытия.
  onCreateMany?: (tasks: string[], ownerId: string, planDate: string) => Promise<void>;
}

const todayAllDay = () => `${dayjs().format('YYYY-MM-DD')}T23:59`;

const QuickTaskModal: React.FC<Props> = ({ preset, isSaving, prefill, onClose, onCreate, onCreateMany }) => {
  const [allDay, setAllDay] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<QuickForm>({ defaultValues: { name: '', region: '', ownerId: '', ownerName: '', planDate: '', arrival: '' } });
  const planDate = watch('planDate');
  const region = watch('region');
  const arrival = watch('arrival');
  const ownerName = watch('ownerName');

  const needsPickDate = preset?.date === 'pick';
  const regionOptions: SelectOption[] = (preset?.regions ?? REGIONS).map(r => ({ value: r, label: r }));
  const needsArrival = !!preset?.arrivalForRegion && region === preset.arrivalForRegion;
  const isMoscowAnketa = preset?.key === 'assign' && region === 'Москва';

  useEffect(() => {
    if (!preset) return;
    reset({
      name: preset.name,
      region: '',
      ownerId: '',
      ownerName: '',
      planDate: preset.date === 'today-allday' ? todayAllDay() : `${dayjs().format('YYYY-MM-DD')}T09:00`,
      arrival: '',
    });
    setAllDay(preset.date === 'today-allday');
  }, [preset, reset]);

  const todayStr = dayjs().format('YYYY-MM-DD');
  const selectedDate = planDate ? planDate.slice(0, 10) : '';
  const minTime = selectedDate === todayStr ? dayjs().format('HH:mm') : undefined;

  const handleToggleAllDay = () => {
    const next = !allDay;
    setAllDay(next);
    const date = planDate ? planDate.slice(0, 10) : todayStr;
    setValue('planDate', next ? `${date}T23:59` : `${date}T09:00`, { shouldValidate: true });
  };

  const submit = handleSubmit(async values => {
    let name = values.name.trim();
    if (preset?.region && values.region) name += ` — ${values.region}`;
    if (preset?.arrivalForRegion && values.region === preset.arrivalForRegion && values.arrival) {
      name += ` (приезд ${dayjs(values.arrival).format('DD.MM.YYYY')})`;
    }
    const ownerId = preset?.owner === 'self' ? (pb.authStore.model?.id ?? '') : values.ownerId;
    const planDate = preset?.date === 'today-allday' ? todayAllDay() : values.planDate;
    await onCreate({ name, ownerId, planDate });
  });

  if (!preset) return null;

  return createPortal(
    <div className="mo open" onClick={isMoscowAnketa ? undefined : onClose}>
      <div
        className={`cm cm-create cm-create-task${isMoscowAnketa ? ' cm-pin-wide' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="cm-top">
          <div>
            {isMoscowAnketa && <div className="cm-overline">ЗАДАЧА АДМИНУ CRM</div>}
            <div className="cm-ti">{preset.label}</div>
            {!isMoscowAnketa && <div className="cm-sub">Быстрое создание задания</div>}
          </div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={18} stroke={2.2} />
          </button>
        </div>

        {isMoscowAnketa ? (
          <div className="cm-pin-body">
            <div className="pin-region-bar">
              <FormField
                className="tcf-field"
                label={<><IconMapPin size={13} stroke={2} />Регион</>}
                labelClassName="tcf-label"
              >
                <Controller
                  control={control}
                  name="region"
                  render={({ field }) => (
                    <Select
                      name="task-region"
                      placeholder="Выберите регион"
                      icon={<IconMapPin size={14} stroke={2} />}
                      options={regionOptions}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormField>
            </div>
            <PinMoscowForm
              prefill={prefill ?? {}}
              departmentOptions={ARRAY}
              isSaving={isSaving}
              onCancel={onClose}
              onSubmit={(tasks, ownerId) =>
                (onCreateMany
                  ? onCreateMany(tasks, ownerId, todayAllDay())
                  : Promise.all(tasks.map(name => onCreate({ name, ownerId, planDate: todayAllDay() }))).then(() => {}))
              }
            />
          </div>
        ) : (
        <form className="cm-create-body cm-create-body-no-scroll" onSubmit={submit}>
          <div className="tcf-fields">
            <FormField
              className="tcf-field"
              label={<><IconFileText size={13} stroke={2} />Название задания</>}
              labelClassName="tcf-label"
              error={errors.name?.message}
            >
              <TextArea
                className="inf-input asgn-modal-name-area"
                placeholder="Что нужно сделать?"
                {...register('name', { validate: v => v.trim() !== '' || 'Укажите название задания' })}
              />
            </FormField>

            {preset.region && (
              <FormField
                className="tcf-field"
                label={<><IconMapPin size={13} stroke={2} />Регион</>}
                labelClassName="tcf-label"
                error={errors.region?.message}
              >
                <Controller
                  control={control}
                  name="region"
                  rules={{ required: 'Выберите регион' }}
                  render={({ field }) => (
                    <Select
                      name="task-region"
                      placeholder="Выберите регион"
                      icon={<IconMapPin size={14} stroke={2} />}
                      options={regionOptions}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormField>
            )}

            {needsArrival && (
              <FormField
                className="tcf-field"
                label={<><IconCalendarEvent size={13} stroke={2} />Дата приезда кандидата</>}
                labelClassName="tcf-label"
                error={errors.arrival?.message}
              >
                <input type="hidden" {...register('arrival', { validate: v => v !== '' || 'Укажите дату приезда' })} />
                <DateInput
                  className="inf-input"
                  name="task-arrival"
                  min={todayStr}
                  value={arrival}
                  onChange={e => setValue('arrival', e.target.value, { shouldValidate: true })}
                />
              </FormField>
            )}

            <div className="tcf-row">
              {preset.owner === 'admin' && (
                <FormField
                  className="tcf-field tcf-field-grow"
                  label={<><IconUser size={13} stroke={2} />Администратор</>}
                  labelClassName="tcf-label"
                  error={errors.ownerId?.message}
                >
                  <Controller
                    control={control}
                    name="ownerId"
                    rules={{ required: 'Выберите администратора' }}
                    render={({ field }) => (
                      <Select
                        name="task-owner"
                        placeholder="Выберите администратора"
                        fetchOptions={fetchManagerOptions}
                        options={field.value && ownerName ? [{ value: field.value, label: ownerName }] : []}
                        value={field.value ?? ''}
                        onChange={(val, opt) => {
                          field.onChange(val);
                          setValue('ownerName', opt?.label ?? '');
                        }}
                        icon={<IconUser size={14} stroke={2} />}
                      />
                    )}
                  />
                </FormField>
              )}

              {needsPickDate && (
                <div className="tcf-field tcf-field-date">
                  <input
                    type="hidden"
                    {...register('planDate', {
                      required: 'Укажите срок',
                      validate: v => dayjs(v).isAfter(dayjs()) || 'Нельзя указать прошедшее время',
                    })}
                  />
                  <div className="asgn-field-header">
                    <label className="tcf-label"><IconClock size={13} stroke={2} />Срок</label>
                    {!preset.noAllDay && (
                      <button
                        className={`asgn-allday-toggle${allDay ? ' active' : ''}`}
                        type="button"
                        onClick={handleToggleAllDay}
                      >
                        <div className="asgn-allday-cb">{allDay && <IconCheck size={9} stroke={3} />}</div>
                        Весь день
                      </button>
                    )}
                  </div>
                  <div className="tcf-datetime">
                    <div className="tcf-datetime-part">
                      <IconCalendarEvent size={13} stroke={2} className="tcf-datetime-icon" />
                      <DateInput
                        className="inf-input"
                        name="task-plan-date"
                        min={todayStr}
                        value={selectedDate}
                        onChange={e => {
                          const time = allDay ? '23:59' : (planDate ? planDate.slice(11) : '09:00');
                          setValue('planDate', e.target.value ? `${e.target.value}T${time}` : '', {
                            shouldValidate: true,
                          });
                        }}
                      />
                    </div>
                    {!allDay && (
                      <div className="tcf-datetime-part">
                        <IconClock size={13} stroke={2} className="tcf-datetime-icon" />
                        <TimePicker
                          className="inf-input"
                          name="task-plan-time"
                          min={minTime}
                          value={planDate ? planDate.slice(11) : ''}
                          onChange={e => {
                            const date = selectedDate || todayStr;
                            setValue('planDate', `${date}T${e.target.value}`, { shouldValidate: true });
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {errors.planDate?.message ? <div className="form-field-error">{errors.planDate.message}</div> : null}
                </div>
              )}
            </div>

            {!needsPickDate && (
              <div className="tcf-field-hint">Срок: сегодня, весь день</div>
            )}
          </div>
          <div className="cm-r-actions" style={{ marginTop: 8, paddingTop: 0 }}>
            <button className="btn-save" type="submit" disabled={isSaving}>
              {isSaving ? 'Создание...' : 'Создать задание'}
            </button>
            <button className="btn-can" type="button" onClick={onClose}>Отмена</button>
          </div>
        </form>
        )}
      </div>
    </div>,
    document.body,
  );
};

export { QuickTaskModal };
